import React, { lazy, Suspense, useState } from 'react'
import { Button, Grid, TextField, Tooltip } from '@mui/material'
import Typography from '@mui/material/Typography'
import Loader from '../../vendor/logoOli.svg'
import Backdrop from '@mui/material/Backdrop'
import { bffService } from '../../services/utils/axios'
import { useUser } from '../../services/contexts'
import { useTranslation } from 'react-i18next'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import {
  HelpCard,
  HelpForm,
  HeaderTypography,
  HelpPageContainer,
} from './style'

const Feedback = lazy(() => import('../../components/Feedback'))
const FailFeedback = lazy(() => import('../../components/DataTable/Feedback'))

const Help = () => {
  const { user } = useUser()
  const { t } = useTranslation()
  const USER_NAME = user?.name
  const USER_EMAIL = user?.email

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({})
  const [selectedFile, setSelectedFile] = useState(null)
  const [error, setError] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState('')

  const handleFileChange = (event) => {
    const file = event.target.files[0]

    // Add your file type validation here if needed
    if (
      file &&
      (file.type === 'application/pdf' || file.type.startsWith('image/'))
    ) {
      setSelectedFile(file)
    } else {
      alert('Please select a valid PDF or image file.')
    }
  }

  const closeFeedback = () => {
    setCompleted(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  const openTooltip = (name) => () => setTooltipOpen(name)
  const closeTooltip = () => setTooltipOpen('')

  const handleSubmit = async (e) => {
    e.preventDefault()

    const { subject, body } = formData

    // Create FormData object for multipart/form-data
    const formDataToSend = new FormData()
    formDataToSend.append('title', USER_EMAIL + ' - ' + subject)
    formDataToSend.append(
      'description',
      USER_NAME + ' - ' + USER_EMAIL + ' - ' + body
    )

    // Add file if selected
    if (selectedFile) {
      formDataToSend.append('file', selectedFile)
    }

    setLoading(true)

    try {
      await bffService.post('/support/ticket', formDataToSend)
      setCompleted(true)
      e.target.reset() // reset form after submit
      setSelectedFile(null) // reset selected file
    } catch (err) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <HelpPageContainer>
      {loading && (
        <Backdrop
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={loading}
        >
          <img src={Loader} alt="loader" className="loader" />
        </Backdrop>
      )}

      <HelpCard>
        <HeaderTypography variant="h1" style={{ paddingBottom: 30 }}>
          {t('LBLSupportTitle')}
        </HeaderTypography>

        <HeaderTypography
          variant="body1"
          style={{ paddingBottom: 20, fontSize: 14 }}
        >
          {t('LBLSupportSubHeading')}
        </HeaderTypography>

        <HelpForm onSubmit={handleSubmit} onChange={handleChange}>
          <Grid container spacing={4} direction="column" alignItems="center">
            <Grid item sx={{ width: '100%' }}>
              <TextField
                required
                id="subject"
                type="text"
                name="subject"
                margin="dense"
                label={t('LBLSubject')}
                sx={{
                  width: '100%',
                  background: '#ffffff',
                }}
                onInvalid={(e) =>
                  e.target.setCustomValidity('Bitte füllen Sie das Feld aus')
                }
                onInput={(e) => e.target.setCustomValidity('')}
              ></TextField>
            </Grid>

            <Grid item sx={{ width: '100%' }}>
              <TextField
                required
                multiline
                type="text"
                minRows="12"
                maxRows="20"
                id="body"
                margin="dense"
                name="body"
                label={t('LBLSupportMessage')}
                sx={{
                  width: '100%',
                  background: '#ffffff',
                }}
                onInvalid={(e) =>
                  e.target.setCustomValidity('Bitte füllen Sie das Feld aus')
                }
                onInput={(e) => e.target.setCustomValidity('')}
              ></TextField>
            </Grid>

            <Grid item sx={{ width: '100%' }}>
              <TextField
                type="file"
                id="attachments"
                margin="dense"
                name="attachments"
                label={t('LBLUploadFile')}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  startAdornment: (
                    <Tooltip
                      open={tooltipOpen === 'attachments'}
                      onOpen={openTooltip('attachments')}
                      onClose={closeTooltip}
                      placement="top-start"
                      title={t('supportFileFormat')}
                    >
                      <InfoIcon
                        color="action"
                        fontSize="small"
                        sx={{ mr: 2 }}
                      />
                    </Tooltip>
                  ),
                }}
                sx={{
                  width: '100%',
                  background: '#ffffff',
                }}
                inputProps={{ accept: '.pdf, .png, .jpg, .jpeg, .heic' }}
                onChange={handleFileChange}
              ></TextField>
            </Grid>

            <Grid item>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ marginTop: 30 }}
              >
                {t('supportSubmit')}
              </Button>
            </Grid>
          </Grid>
        </HelpForm>
      </HelpCard>

      <Suspense fallback={<div />}>
        <Feedback
          open={completed}
          title={t('LBLRequestSent')}
          message={
            <>
              <Typography align="center">
                {t('supportSuccessFeedback')}
              </Typography>
            </>
          }
          handleClose={closeFeedback}
        />
        <FailFeedback
          open={error}
          title={t('mistake')}
          severity="warning"
          message={
            <>
              <Typography align="center">
                {t('LBLSomethingwentWrong')}
              </Typography>
            </>
          }
          handleClose={() => {
            setError(false)
          }}
        />
      </Suspense>
    </HelpPageContainer>
  )
}
export default Help
